"use client";

import { useEffect, useState } from "react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import Link from "next/link";
import {
  Check,
  Star,
  MapPin,
  Wifi,
  Coffee,
  Bed,
  Users,
  Utensils,
  Wind,
  Sparkles,
  Phone,
  Mail,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Home,
  ChevronRight,
  ChevronLeft,
  Calendar,
  ShieldAlert,
  Bus,
  Compass,
  HelpCircle,
  Activity,
  ArrowRight,
  Clock,
  Award,
  Quote,
  AlertCircle,
} from "lucide-react";
import Slider from "react-slick";
import "../styles/carousel.css";
import dynamic from "next/dynamic";

const ImageGalleryModal = dynamic(() => import("./ImageGalleryModal"), {
  ssr: false,
});
import {
  fetchResortBySlug,
  insertBookingReference,
} from "@/services/resort.services";
import { IResortDetail } from "@/types/resort.types";
import { useAppDispatch } from "@/hooks/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { insertWeblInquiry } from "@/services/hotel.services";
const InquiryDialog = dynamic(() => import("./InquiryDialog"), { ssr: false });

interface ResortDetailPageNewProps {
  resortSlug: string;
  initialResort?: IResortDetail | null;
  initialCMSData?: Record<string, any>;
}

// Custom structure for parsed list content to enforce elegant typography and high contrast
const DynamicParsedHtml = ({ html }: { html: string }) => {
  return (
    <div
      className="text-slate-700 leading-relaxed font-normal text-sm space-y-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:mt-2 [&_ul]:space-y-3 [&_li]:relative [&_li]:pl-6 [&_li::before]:content-['✓'] [&_li::before]:absolute [&_li::before]:left-0 [&_li::before]:text-pink-600 [&_li::before]:font-bold [&_p]:mb-2"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Slider Arrows matching home page testimonials
function NextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Next slide"
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  );
}

// PrevArrow matching home page testimonials
function PrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
}

export default function ResortDetailPageNew({
  resortSlug,
  initialResort = null,
  initialCMSData = {},
}: ResortDetailPageNewProps) {
  const [resort, setResort] = useState<IResortDetail | null>(initialResort);
  const [loading, setLoading] = useState(!initialResort);
  const [error, setError] = useState<string | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Itinerary tab selected indexes
  const [activeItineraryIndex, setActiveItineraryIndex] = useState(0);
  const [activeDayIndex, setActiveDayIndex] = useState(0);

  const [activeInfoTab, setActiveInfoTab] = useState(""); // Dynamically compile rooms from resort.RoomCategoryList
  const dispatch = useAppDispatch();
  const router = useRouter();

  // Dynamic slidesCount — reads actual window.innerWidth to fix SSR + mobile breakpoint issue
  const getSlidesToShow = (max: number) => {
    if (typeof window === "undefined") return max;
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 768) return Math.min(max, 2);
    if (w <= 1024) return Math.min(max, 3);
    return max;
  };
  const [slidesCount, setSlidesCount] = useState(() => ({
    testimonials: getSlidesToShow(2),
    packages: getSlidesToShow(3),
    rooms: getSlidesToShow(3),
    activities: getSlidesToShow(4),
    attractions: getSlidesToShow(3),
  }));
  useEffect(() => {
    const onResize = () =>
      setSlidesCount({
        testimonials: getSlidesToShow(2),
        packages: getSlidesToShow(3),
        rooms: getSlidesToShow(3),
        activities: getSlidesToShow(4),
        attractions: getSlidesToShow(3),
      });
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const formik = useFormik({
    initialValues: {
      FirstName: "",
      EmailID: "",
      MobileNo: "",
      CheckIn: "",
      Nights: "2 Nights",
      Adults: "2",
      Child: "0",
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      FirstName: Yup.string().trim().required("Full Name is required"),
      MobileNo: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
      EmailID: Yup.string()
        .required("Email is required")
        .email("Invalid email format"),
      CheckIn: Yup.string().required("Check-in date is required"),
      Nights: Yup.string().required("Nights is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        FirstName: values.FirstName,
        LastName: "",
        CompanyName: "",
        Address: "",
        Email: values.EmailID,
        MobileNo: String(values.MobileNo),
        NoofPerson: +values.Adults,
        NoofChild: +values.Child,
        CheckinDate: values.CheckIn,
        TypeYourTent: resort?.Title || "",
        ResortURL: resortSlug || resort?.URL || "",
        PackageURL: "",
        Remarks: "",
        Nights: values.Nights,
      };

      try {
        const response = await dispatch(insertWeblInquiry(payload));
        if (response.meta.requestStatus === "fulfilled") {
          resetForm();
          setTimeout(() => {
            router.push("/thankyou/");
          }, 200);
        } else {
          toast.error(
            (response.payload as any)?.message ||
              "Failed to submit inquiry. Please try again.",
          );
        }
      } catch (err) {
        console.error(err);
        toast.error("An unexpected error occurred. Please try again later.");
      }
    },
  });

  const getDynamicRooms = (resortData: IResortDetail | null) => {
    if (!resortData) return [];
    const apiRooms = resortData.RoomCategoryList || [];
    return apiRooms
      .filter((rc: any) => rc.IsActive)
      .map((rc: any) => ({
        Title: rc.Title,
        Description: rc.Description,
        MainImage: rc.MainImage,
      }));
  };

  // Load Resort details on mount / slug change
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<any>(null);

  const handleInquiryOpen = (pkg: any) => {
    setSelectedPkg(pkg);
    setInquiryOpen(true);
  };

  useEffect(() => {
    // If we already have server-pre-fetched data for this slug, skip the API call
    if (initialResort && initialResort.URL === resortSlug) return;

    let isMounted = true;
    async function loadResortData() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchResortBySlug(resortSlug);
        if (isMounted) {
          setResort(data);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(
            err?.message ||
              "Something went wrong while fetching resort details.",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    loadResortData();
    return () => {
      isMounted = false;
    };
  }, [resortSlug]);

  // Safe helper to extract and clean map URL src from iframe html
  const cleanMapUrl = (mapUrlString: string) => {
    if (!mapUrlString) return "";
    if (mapUrlString.includes('src="')) {
      const match = mapUrlString.match(/src="([^"]+)"/);
      if (match && match[1]) {
        return match[1];
      }
    }
    return mapUrlString;
  };

  // Helper to compile a photo gallery
  const getResortGallery = (resortDataObj: IResortDetail) => {
    const list: string[] = [];

    // First include the gallery list from API
    if (resortDataObj.GalleryImages && resortDataObj.GalleryImages.length > 0) {
      resortDataObj.GalleryImages.forEach((item) => {
        if (item.GalleryImage) list.push(item.GalleryImage);
      });
    }

    // Push BannerImage to start of the list if available
    if (
      resortDataObj.BannerImage &&
      !list.includes(resortDataObj.BannerImage)
    ) {
      list.unshift(resortDataObj.BannerImage);
    }

    return list;
  };

  // Strip HTML utility matching home page testimonials text processor
  const getCleanText = (htmlContent: string) => {
    if (!htmlContent) return "";
    let cleaned = htmlContent
      .replace(/<[^>]*>/g, "") // Strip HTML tags
      .replace(/\s+/g, " ") // Collapse multiple spaces
      .replace(/\s+([.,!?;:])/g, "$1") // Remove spaces before punctuation
      .trim();
    if (cleaned.length > 250) {
      cleaned = cleaned.substring(0, 250).trim() + "...";
    }
    return cleaned;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <div className="w-16 h-16 border-4 border-pink-100 border-t-pink-600 rounded-full animate-spin mb-4"></div>
        <p className="text-slate-700 font-semibold tracking-wide text-sm">
          Gathering premium resort details...
        </p>
      </div>
    );
  }

  if (error || !resort) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 text-center">
        <ShieldAlert className="w-16 h-16 text-rose-500 mb-4" />
        <h1 className="text-3xl text-slate-800 font-bold mb-2">
          Unable to Retrieve Resort
        </h1>
        <p className="text-slate-600 max-w-md font-medium text-sm mb-6">
          {error ||
            "The requested resort slug is not registered or the API server returned an empty dataset."}
        </p>
        <Link
          href="/"
          className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold text-sm px-8 py-3 rounded-full shadow-sm hover:shadow transition duration-300"
        >
          Return to Homepage
        </Link>
      </div>
    );
  }

  const gallery = getResortGallery(resort);
  const rooms = getDynamicRooms(resort);

  // Compile active policy info tabs dynamically
  interface IInfoTab {
    id: string;
    title: string;
    content: string;
    isCancellation?: boolean;
  }
  const infoTabs: IInfoTab[] = [];
  if (resort) {
    if (resort.Inclusion && resort.Inclusion.trim() !== "") {
      infoTabs.push({
        id: "inclusions",
        title: "Stay Inclusions",
        content: resort.Inclusion,
      });
    }
    if (resort.SightseeingDetails && resort.SightseeingDetails.trim() !== "") {
      infoTabs.push({
        id: "sightseeing",
        title: "Sightseeing Guidelines",
        content: resort.SightseeingDetails,
      });
    }
    if (resort.TransferDetails && resort.TransferDetails.trim() !== "") {
      infoTabs.push({
        id: "transfers",
        title: "Transfer Details & Schedules",
        content: resort.TransferDetails,
      });
    }
    if (resort.CancellationPolicy && resort.CancellationPolicy.trim() !== "") {
      infoTabs.push({
        id: "cancellation",
        title: "Cancellation Policy",
        content: resort.CancellationPolicy,
        isCancellation: true,
      });
    }
  }

  const activeTabObj =
    infoTabs.find((t) => t.id === activeInfoTab) || infoTabs[0];

  // Dynamic Packages Array mapping
  const displayPackages =
    resort.ResortPackageList && resort.ResortPackageList.length > 0
      ? resort.ResortPackageList.map((pkg) => {
          let daysText = "2N / 3D";
          if (
            pkg.Title.toLowerCase().includes("1 night") ||
            pkg.Title.toLowerCase().includes("2 days")
          ) {
            daysText = "1N / 2D";
          } else if (
            pkg.Title.toLowerCase().includes("3 night") ||
            pkg.Title.toLowerCase().includes("4 days")
          ) {
            daysText = "3N / 4D";
          }
          return {
            title: pkg.Title,
            url: pkg.URL,
            BookingURL: pkg.BookingURL,
            shortDescription: pkg.shortDescription,
            price: pkg.Price,
            PricePer: pkg.PricePer,
            image: pkg.MainImage || "",
            daysText,
          };
        })
      : [];

  // Dynamic Amenities List matching API payload
  const displayAmenities =
    resort.AmenitiesList && resort.AmenitiesList.length > 0
      ? resort.AmenitiesList
      : [];

  // Dynamic Testimonials Carousel mapping
  const displayTestimonials =
    resort.TestimonialList && resort.TestimonialList.length > 0
      ? resort.TestimonialList.map((t) => ({
          name: t.CustomerName,
          type: t.UniversityName || "Verified Traveler Stays",
          image: t.TestimonialImage || "",
          rating: 5,
          text: getCleanText(t.Description),
          package: "",
        }))
      : [];

  // Dynamic Activities Grid mapping
  const displayActivities =
    resort.ActivityList && resort.ActivityList.length > 0
      ? resort.ActivityList.map((act) => ({
          title: act.Title,
          image: act.ImageUpload,
          tag: "",
        }))
      : [];

  // Dynamic FAQ Accordion mapping
  const displayFaqs =
    resort.FAQList && resort.FAQList.length > 0
      ? resort.FAQList.map((f) => ({
          question: f.Question,
          answer: getCleanText(f.Answer),
        }))
      : [];

  const testimonialSliderSettings = {
    dots: true,
    infinite: displayTestimonials.length > slidesCount.testimonials,
    speed: 500,
    slidesToShow: slidesCount.testimonials,
    slidesToScroll: 1,
    autoplay: displayTestimonials.length > slidesCount.testimonials,
    autoplaySpeed: 5000,
    swipe: true,
    touchMove: true,
    arrows: slidesCount.testimonials > 1 && displayTestimonials.length > 1,
    nextArrow:
      slidesCount.testimonials > 1 && displayTestimonials.length > 1 ? (
        <NextArrow />
      ) : undefined,
    prevArrow:
      slidesCount.testimonials > 1 && displayTestimonials.length > 1 ? (
        <PrevArrow />
      ) : undefined,
  };

  const packagesSliderSettings = {
    dots: true,
    infinite: displayPackages.length > slidesCount.packages,
    speed: 500,
    slidesToShow: slidesCount.packages,
    slidesToScroll: 1,
    autoplay: displayPackages.length > slidesCount.packages,
    autoplaySpeed: 5000,
    swipe: true,
    touchMove: true,
    arrows: slidesCount.packages > 1 && displayPackages.length > 1,
    nextArrow:
      slidesCount.packages > 1 && displayPackages.length > 1 ? (
        <NextArrow />
      ) : undefined,
    prevArrow:
      slidesCount.packages > 1 && displayPackages.length > 1 ? (
        <PrevArrow />
      ) : undefined,
  };

  // CMS sections come from the server-fetched initialCMSData lookup object
  const section1 = Object.values(initialCMSData).find((item: any) =>
    item.Name?.toLowerCase().includes("section 1"),
  );

  const section2 = Object.values(initialCMSData).find((item: any) =>
    item.Name?.toLowerCase().includes("section 2"),
  );

  const handleBookNow = (pkg: any) => {
    insertBookingReference(0, window.location.href).catch(() => {});
    if (pkg.BookingURL) {
      window.open(pkg.BookingURL, "_blank");
    } else {
      handleInquiryOpen(pkg);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 pb-20 font-sans antialiased text-gray-800">
      <Toaster position="top-right" richColors />
      <InquiryDialog
        isOpen={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        contextName={selectedPkg?.title}
        contextURL={selectedPkg?.url}
        inquiryType="Rann Packages"
      />
      {/* ────────────────── DYNAMIC API SECTION: HERO HEADER ────────────────── */}
      <section className="relative overflow-hidden bg-gray-950 h-[450px] md:h-[550px] w-full">
        <ImageWithFallback
          src={resort.BannerImage || "/placeholder.png"}
          alt={resort.Title}
          className="w-full h-full object-cover opacity-80 scale-100 hover:scale-102 transition-transform duration-700"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-4xl text-white pb-10">
              <span className="inline-block bg-pink-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-md">
                {resort.TentCityName || "Tent City Resort"}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-wide mb-4 text-white leading-tight drop-shadow-md">
                {resort.Title}
              </h1>
              <p className="text-gray-100 font-medium text-base sm:text-lg leading-relaxed line-clamp-3 max-w-2xl mb-6 drop-shadow-sm">
                {resort.ShortDescription}
              </p>
              {/* <div className="flex flex-wrap items-center gap-4 text-sm font-medium">
                <div className="flex items-center gap-2 text-gray-100 bg-black/35 backdrop-blur-md px-3.5 py-2 rounded-xl">
                  <MapPin className="w-4 h-4 text-pink-500" />
                  <span>{resort.TentCityName || "Dhordo, Kutch"}</span>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── BREADCRUMB NAVIGATION ────────────────── */}
      <div className="bg-white border-b border-gray-100 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
            <Link
              href="/"
              className="hover:text-pink-600 transition-colors flex items-center gap-1 cursor-pointer"
            >
              <Home className="w-3.5 h-3.5" />
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-500">
              {resort.TentCityName || "Kutch Stays"}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-900 font-semibold">{resort.Title}</span>
          </div>
        </div>
      </div>

      {/* <section className="py-20 bg-gray-50/50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="rounded-2xl overflow-hidden shadow-md" style={{ backgroundColor: "#fdf2f8" }}>
              <div className="relative pt-3 pb-3 px-4" style={{ backgroundColor: "#000000" }}>
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                </div>
                <h2 className="text-center text-2xl font-extrabold tracking-wide mt-2" style={{ color: "#ffffff" }}>
                  NOVEMBER
                </h2>
                <div className="mt-2" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Mon
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Tue
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Wed
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Thu
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Fri
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sat
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sun
                  </span>
                </div>
              </div>
              <div className="p-3">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      10
                    </span>
                  </div>
                  <div className="h-9" />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      11
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      12
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      13
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      14
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      15
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      16
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      17
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      18
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      19
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      20
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      21
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      22
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      23
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      24
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      25
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      26
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      27
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      28
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      29
                    </span>
                  </div>
                  <div className="h-9" />
                  <div className="h-9" />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      30
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md" style={{ backgroundColor: "#fdf2f8" }}>
              <div className="relative pt-3 pb-3 px-4" style={{ backgroundColor: "#000000" }}>
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                </div>
                <h2 className="text-center text-2xl font-extrabold tracking-wide mt-2" style={{ color: "#ffffff" }}>
                  DECEMBER
                </h2>
                <div className="mt-2" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Mon
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Tue
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Wed
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Thu
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Fri
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sat
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sun
                  </span>
                </div>
              </div>
              <div className="p-3">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="h-9" />
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      1
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      2
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      3
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      4
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      5
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      6
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      7
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      8
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      9
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      10
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      11
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      12
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      13
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      14
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      15
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      16
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      17
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      18
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      19
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      20
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      21
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      22
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      23
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      24
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      25
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      26
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      27
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      28
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      29
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      30
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      31
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md" style={{ backgroundColor: "#fdf2f8" }}>
              <div className="relative pt-3 pb-3 px-4" style={{ backgroundColor: "#000000" }}>
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                </div>
                <h2 className="text-center text-2xl font-extrabold tracking-wide mt-2" style={{ color: "#ffffff" }}>
                  JANUARY
                </h2>
                <div className="mt-2" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Mon
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Tue
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Wed
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Thu
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Fri
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sat
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sun
                  </span>
                </div>
              </div>
              <div className="p-3">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      1
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      2
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      3
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      4
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      5
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      6
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      7
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      8
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      9
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      10
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      11
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      12
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      13
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      14
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      15
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      16
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      17
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      18
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      19
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      20
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      21
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      22
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      23
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      24
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      25
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      26
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      27
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      28
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      29
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      30
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      31
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md" style={{ backgroundColor: "#fdf2f8" }}>
              <div className="relative pt-3 pb-3 px-4" style={{ backgroundColor: "#000000" }}>
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: "#fbcfe8", border: "2px solid #f472b6" }} />
                </div>
                <h2 className="text-center text-2xl font-extrabold tracking-wide mt-2" style={{ color: "#ffffff" }}>
                  FEBRUARY
                </h2>
                <div className="mt-2" style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Mon
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Tue
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Wed
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Thu
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Fri
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sat
                  </span>
                  <span className="font-semibold text-center" style={{ color: "#ffffff", fontSize: "11px", opacity: 0.9 }}>
                    Sun
                  </span>
                </div>
              </div>
              <div className="p-3">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="h-9" />
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      1
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      2
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      3
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      4
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      5
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      6
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      7
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      8
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      9
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      10
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      11
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      12
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      13
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      14
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      15
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      16
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      17
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      18
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      19
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      20
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#f97316" }}>
                      21
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      22
                    </span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      23
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      24
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#7e22ce" }}>
                      25
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      26
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      27
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm" style={{ backgroundColor: "#047857" }}>
                      28
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-10 mt-12">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl overflow-hidden shadow-md" style={{ width: '24px', height: '24px', backgroundColor: '#f97316' }} />
              <span className="text-gray-900 font-extrabold tracking-wide text-sm md:text-base">
                PREMIUM DATES
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl overflow-hidden shadow-md" style={{ width: '24px', height: '24px', backgroundColor: '#047857' }} />
              <span className="text-gray-900 font-extrabold tracking-wide text-sm md:text-base">
                REGULAR DATES
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl overflow-hidden shadow-md" style={{ width: '24px', height: '24px', backgroundColor: '#7e22ce' }} />
              <span className="text-gray-900 font-extrabold tracking-wide text-sm md:text-base">
                ECONOMY DATES
              </span>
            </div>
          </div>
        </div>
      </section> */}

      {/* ────────────────── DYNAMIC API SECTION: RESORT PACKAGE LIST (FLAT) ────────────────── */}
      {displayPackages.length > 0 && (
        <section className="py-20 bg-gray-50/50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Find Your Perfect Package
              </h2>
              <p className="text-gray-600 font-normal text-sm">
                Select one of our premium vacation plans to discover the
                mesmerizing salt plains in optimal luxury.
              </p>
            </div>

            {displayPackages.length > 3 ? (
              <div className="relative px-6">
                <Slider {...packagesSliderSettings}>
                  {displayPackages.map((pkg, idx) => (
                    <div key={idx} className="px-3 pb-4">
                      <div className="group rounded-2xl border border-gray-200 bg-white p-6 flex flex-col justify-between hover:border-pink-200 hover:shadow-lg transition-all duration-300 h-[460px]">
                        <div>
                          <div className="flex justify-between items-start mb-4">
                            <span className="bg-pink-50 text-pink-600 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded font-bold">
                              Package Stay
                            </span>
                            <span className="text-xs font-semibold text-gray-500">
                              {pkg.daysText}
                            </span>
                          </div>
                          {pkg.image && (
                            <div className="h-40 w-full overflow-hidden rounded-xl bg-slate-100 mb-4">
                              <Link
                                href={`/package/${pkg.url}`}
                                className="block w-full h-full"
                              >
                                <ImageWithFallback
                                  src={pkg.image}
                                  alt={pkg.title}
                                  className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                                />
                              </Link>
                            </div>
                          )}
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors line-clamp-1">
                            <Link href={`/package/${pkg.url}`}>
                              {pkg.title}
                            </Link>
                          </h3>
                          <p className="text-gray-600 text-xs font-normal leading-relaxed mb-4 line-clamp-3">
                            {pkg.shortDescription}
                          </p>
                        </div>
                        <div>
                          <div className="flex items-baseline gap-1 mb-5">
                            <span className="text-gray-500 text-xs font-medium">
                              From
                            </span>
                            <span className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-rose-600 bg-clip-text text-transparent">
                              ₹{pkg.price.toLocaleString("en-IN")}
                            </span>
                            <span className="text-gray-500 text-xs font-medium">
                              /{" "}
                              {pkg.PricePer
                                ? pkg.PricePer.replace(/^\/+/, "").trim()
                                : "Per Person"}
                            </span>
                          </div>
                          <div className="flex gap-2.5">
                            <button
                              onClick={() => handleBookNow(pkg)}
                              className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition text-sm cursor-pointer font-sans font-medium"
                            >
                              Book Now
                            </button>
                            <button
                              onClick={() => handleInquiryOpen(pkg)}
                              className="flex-1 text-center border-2 border-pink-500 text-pink-600 px-4 py-2 rounded-lg hover:bg-pink-50 transition text-sm cursor-pointer font-sans font-medium flex items-center justify-center"
                            >
                              Inquiry Now
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            ) : (
              <div
                className={`grid grid-cols-1 gap-8 ${
                  displayPackages.length === 1
                    ? "max-w-md mx-auto"
                    : displayPackages.length === 2
                      ? "md:grid-cols-2 max-w-4xl mx-auto"
                      : "md:grid-cols-3 max-w-7xl mx-auto"
                }`}
              >
                {displayPackages.map((pkg, idx) => (
                  <div
                    key={idx}
                    className="group rounded-2xl border border-gray-200 bg-white p-6 flex flex-col justify-between hover:border-pink-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <span className="bg-pink-50 text-pink-600 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded font-bold">
                          Package Stay
                        </span>
                        <span className="text-xs font-semibold text-gray-500">
                          {pkg.daysText}
                        </span>
                      </div>
                      {pkg.image && (
                        <div className="h-40 w-full overflow-hidden rounded-xl bg-slate-100 mb-4">
                          <Link
                            href={`/package/${pkg.url}`}
                            className="block w-full h-full"
                          >
                            <ImageWithFallback
                              src={pkg.image}
                              alt={pkg.title}
                              className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                            />
                          </Link>
                        </div>
                      )}
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
                        <Link href={`/package/${pkg.url}`}>{pkg.title}</Link>
                      </h3>
                      {/* <p className="text-gray-600 text-xs font-normal leading-relaxed mb-6">
                        Enjoy an immersive stay with organic local buffet meals,
                        guided desert tours, and premium cultural performances.
                      </p> */}
                    </div>
                    <div className="flex items-baseline gap-1 mb-5">
                      <span className="text-gray-500 text-xs font-medium">
                        From
                      </span>
                      <span className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-rose-600 bg-clip-text text-transparent">
                        ₹{pkg.price.toLocaleString("en-IN")}
                      </span>
                      <span className="text-gray-500 text-xs font-medium">
                        /{" "}
                        {pkg.PricePer
                          ? pkg.PricePer.replace(/^\/+/, "").trim()
                          : "Per Person"}
                      </span>
                    </div>
                    <div className="flex gap-2.5">
                      <button
                        onClick={() => handleBookNow(pkg)}
                        className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition text-sm cursor-pointer font-sans font-medium"
                      >
                        Book Now
                      </button>
                      <button
                        onClick={() => handleInquiryOpen(pkg)}
                        className="flex-1 text-center border-2 border-pink-500 text-pink-600 px-4 py-2 rounded-lg hover:bg-pink-50 transition text-sm cursor-pointer font-sans font-medium flex items-center justify-center animate-none"
                      >
                        Inquiry Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ────────────────── STATIC CMS SECTION 2: EXPERIENCE HEADER / ABOUT BLOCK (FLAT) ────────────────── */}
      {section1 && (
        <section className="py-20 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-64 lg:h-auto min-h-[420px] bg-gray-100 overflow-hidden rounded-3xl shadow-lg border border-gray-100">
                <ImageWithFallback
                  src={section1.CMSImage}
                  alt={section1.CMSImageAlterTag}
                  width={1000}
                  height={600}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              <div
                className="flex flex-col justify-center select-text"
                dangerouslySetInnerHTML={{ __html: section1.Template }}
              />
            </div>
          </div>

          {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-64 lg:h-auto min-h-[420px] bg-gray-900 overflow-hidden rounded-3xl shadow-lg border border-gray-100">
                <ImageWithFallback
                  src="https://images.novotrips.com/SOLARMITRA/Uploads/Ranutsav/documents/639149729581355949.jpg"
                  alt="Dhordo Tent City Row View"
                  className="w-full h-full object-cover opacity-90 transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950/65 via-transparent to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="flex items-center gap-2.5 mb-2">
                    <Award className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 text-xs font-semibold uppercase tracking-widest">
                      Premium Quality Certified
                    </span>
                  </div>
                  <h4 className="text-white text-lg font-medium tracking-wide">
                    "The salt flats reflect our ancient heritage and infinite
                    hospitality."
                  </h4>
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <h2 className="text-2xl text-gray-900 mb-6 font-semibold">
                  Dhordo Tent City: Experience the Magic of White Rann
                </h2>
                <div className="text-gray-700 leading-relaxed font-normal text-sm space-y-4">
                  <p>
                    Dhordo Tent City is the heart of the colourful Rann Utsav,
                    providing a memorable holiday in Gujarat's breathtaking
                    White Desert. Designed to combine luxury and tradition, the
                    tent city brings you in local culture, folk music, dance,
                    and handcrafted art, all under a vast desert sky.
                  </p>
                  <p>
                    Whether you're travelling for a weekend or the entire
                    festival, Rann Utsav Tent City Dhordo promises an
                    unforgettable experience. From high-quality tents to
                    well-planned activities, everything is designed to ensure
                    your comfort and joy.
                  </p>
                  <p>
                    Planning your trip is made easy with the various Dhordo Tent
                    City booking options and clear pricing. Instead of looking
                    for hotels near Tent City Dhordo, stay where the festivities
                    begin.
                  </p>
                  <p className="pt-2">
                    Step into The Tent City Dhordo and make memories in the
                    enchanting area of Kutch.{" "}
                    <Link
                      href="#booking-sidebar"
                      className="text-pink-600 font-bold hover:underline"
                    >
                      Contact us
                    </Link>{" "}
                    today to discover the ideal package for your Rann Utsav
                    getaway!
                  </p>
                </div>
              </div>
            </div>
          </div> */}
        </section>
      )}

      {/* ────────────────── STATIC CMS SECTION 3: TARIFF TABLES (FLAT) ────────────────── */}
      {section2 && (
        <section className="py-20 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 select-text">
            <div dangerouslySetInnerHTML={{ __html: section2.Template }} />
          </div>

          {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-3 font-semibold">
                Rann Utsav Dhordo Tent City Price List
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Find the best Dhordo Tent City price options right here —
                transparent, affordable, and tailored to suit every traveller's
                comfort and style.
              </p>
            </div>

            <div className="space-y-12">
              <div>
                <div className="bg-[#c21a78] text-white text-center py-3.5 px-4 rounded-t-xl font-semibold text-sm sm:text-base tracking-wide shadow-sm">
                  Tariff for Oct, Nov, Feb & March 2025-26 (Excluding Full Moon
                  & Diwali)
                </div>
                <div className="overflow-x-auto border-x border-b border-gray-200 rounded-b-xl">
                  <table className="w-full border-collapse text-left text-sm min-w-[700px]">
                    <thead>
                      <tr className="bg-gray-50 text-gray-800 font-semibold text-xs uppercase border-b border-gray-150">
                        <th className="p-4 border-r border-gray-150">
                          Rates (INR) Per Person
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          1 Night
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          2 Nights
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          3 Nights
                        </th>
                        <th className="p-4 text-center">
                          Extra Mattress Per Night
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 text-sm">
                      <tr className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150">
                          Super Premium Tent
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹9,900*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹19,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹27,500*
                        </td>
                        <td className="p-4 text-center text-gray-400 border-r border-gray-150">
                          -
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50/50 transition-colors bg-pink-50/5">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150">
                          Premium Tent
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹8,800*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹17,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹25,000*
                        </td>
                        <td className="p-4 text-center font-medium text-pink-600">
                          ₹5,250*
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150">
                          Deluxe AC Swiss Cottage
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹7,900*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹15,500*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹22,000*
                        </td>
                        <td className="p-4 text-center text-gray-400">-</td>
                      </tr>
                      <tr className="hover:bg-gray-50/50 transition-colors bg-pink-50/5">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150">
                          Non AC Swiss Cottage
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹5,900*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹11,500*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹16,000*
                        </td>
                        <td className="p-4 text-center font-medium text-pink-600">
                          ₹4,250*
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="bg-[#c21a78] text-white text-center py-3.5 px-4 rounded-t-xl font-semibold text-sm sm:text-base tracking-wide shadow-sm">
                  Additional Charges
                </div>
                <div className="overflow-x-auto border-x border-b border-gray-200 rounded-b-xl">
                  <table className="w-full border-collapse text-left text-sm min-w-[700px]">
                    <thead>
                      <tr className="bg-gray-50 text-gray-800 font-semibold text-xs uppercase border-b border-gray-150">
                        <th className="p-4 border-r border-gray-150">
                          Rates (INR) Per Person
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          1 Night
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          2 Nights
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          3 Nights
                        </th>
                        <th className="p-4 text-center">
                          Extra Mattress Per Night
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 text-sm">
                      <tr className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150 max-w-sm">
                          Additional Tariff for Oct, Nov, Feb & March (Full Moon
                          Diwali: 23rd to 26th Oct) Dec & Jan (Excluding Full
                          Moon & Christmas Week)
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹2,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹3,500*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹4,500*
                        </td>
                        <td className="p-4 text-center font-medium text-pink-600 border-r border-gray-150 max-w-xs">
                          ₹5,750*{" "}
                          <span className="text-[10px] text-gray-500 block font-normal">
                            (Super Premium, Premium, Deluxe AC Swiss Cottage)
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50/50 transition-colors bg-pink-50/5">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150 max-w-sm">
                          Additional Tariff for Dec & Jan (19th Dec to 1st Jan
                          2026) & Full Moon (Dec & Jan)
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹4,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹5,500*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹7,500*
                        </td>
                        <td className="p-4 text-center font-medium text-pink-600 max-w-xs">
                          ₹4,750*{" "}
                          <span className="text-[10px] text-gray-500 block font-normal">
                            (Non-AC Swiss Cottage)
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="text-center font-bold text-xl sm:text-2xl text-pink-600 mb-4">
                  23rd October 2025 to 4th March 2026
                </div>
                <div className="bg-[#c21a78] text-white text-center py-3.5 px-4 rounded-t-xl font-semibold text-sm sm:text-base tracking-wide shadow-sm">
                  All Days
                </div>
                <div className="overflow-x-auto border-x border-b border-gray-200 rounded-b-xl">
                  <table className="w-full border-collapse text-left text-sm min-w-[700px]">
                    <thead>
                      <tr className="bg-gray-50 text-gray-800 font-semibold text-xs uppercase border-b border-gray-150">
                        <th className="p-4 border-r border-gray-150">
                          Rates (INR)
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          1 Night
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          2 Nights
                        </th>
                        <th className="p-4 text-center border-r border-gray-150">
                          3 Nights
                        </th>
                        <th className="p-4 text-center">
                          Extra Mattress Per Night
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 text-sm">
                      <tr className="hover:bg-gray-50/50 transition-colors">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150">
                          Darbari Suite (4 Pax)
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹70,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹1,40,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹2,10,000*
                        </td>
                        <td className="p-4 text-center font-medium text-pink-600 border-r border-gray-150">
                          ₹7,750*
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50/50 transition-colors bg-pink-50/5">
                        <td className="p-4 font-semibold text-gray-900 border-r border-gray-150">
                          Rajwadi Suite (2 Pax)
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹35,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹70,000*
                        </td>
                        <td className="p-4 text-center font-medium text-gray-800 border-r border-gray-150">
                          ₹1,05,000*
                        </td>
                        <td className="p-4 text-center text-gray-400">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div> */}
        </section>
      )}

      {/* ────────────────── CALENDAR & TARIFF DATES SECTION ────────────────── */}
        {/* <div className="mt-6 space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            <div
              className="rounded-2xl overflow-hidden shadow-md"
              style={{ backgroundColor: "rgb(253, 242, 248)" }}
            >
              <div
                className="relative pt-3 pb-3 px-4"
                style={{ backgroundColor: "rgb(0, 0, 0)" }}
              >
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: "rgb(251, 207, 232)",
                        border: "2px solid rgb(244, 114, 182)",
                      }}
                    ></div>
                  ))}
                </div>
                <h2
                  className="text-center text-2xl font-extrabold tracking-wide mt-4"
                  style={{ color: "rgb(255, 255, 255)" }}
                >
                  NOVEMBER
                </h2>
                <div className="mt-2 grid grid-cols-7">
                  {["SU", "MO", "TU", "WE", "TH", "FR", "SA"].map((d, i) => (
                    <span
                      key={d}
                      className="font-semibold text-center"
                      style={{
                        color:
                          i === 0 ? "rgb(239, 68, 68)" : "rgb(255, 255, 255)",
                        fontSize: "11px",
                        opacity: i === 0 ? 1 : 0.9,
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="grid grid-cols-7">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[8, 9, 10, 11, 12, 13, 14].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[15, 16, 17, 18, 19, 20, 21].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[22, 23, 24, 25].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  {[26, 27, 28].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[29, 30].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl overflow-hidden shadow-md"
              style={{ backgroundColor: "rgb(253, 242, 248)" }}
            >
              <div
                className="relative pt-3 pb-3 px-4"
                style={{ backgroundColor: "rgb(0, 0, 0)" }}
              >
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: "rgb(251, 207, 232)",
                        border: "2px solid rgb(244, 114, 182)",
                      }}
                    ></div>
                  ))}
                </div>
                <h2
                  className="text-center text-2xl font-extrabold tracking-wide mt-4"
                  style={{ color: "rgb(255, 255, 255)" }}
                >
                  DECEMBER
                </h2>
                <div className="mt-2 grid grid-cols-7">
                  {["SU", "MO", "TU", "WE", "TH", "FR", "SA"].map((d, i) => (
                    <span
                      key={d}
                      className="font-semibold text-center"
                      style={{
                        color:
                          i === 0 ? "rgb(239, 68, 68)" : "rgb(255, 255, 255)",
                        fontSize: "11px",
                        opacity: i === 0 ? 1 : 0.9,
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="grid grid-cols-7">
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[13, 14, 15, 16, 17].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  {[18, 19].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(249, 115, 22)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[20, 21, 22, 23, 24, 25, 26].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(249, 115, 22)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[27, 28, 29, 30, 31].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(249, 115, 22)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl overflow-hidden shadow-md"
              style={{ backgroundColor: "rgb(253, 242, 248)" }}
            >
              <div
                className="relative pt-3 pb-3 px-4"
                style={{ backgroundColor: "rgb(0, 0, 0)" }}
              >
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: "rgb(251, 207, 232)",
                        border: "2px solid rgb(244, 114, 182)",
                      }}
                    ></div>
                  ))}
                </div>
                <h2
                  className="text-center text-2xl font-extrabold tracking-wide mt-4"
                  style={{ color: "rgb(255, 255, 255)" }}
                >
                  JANUARY
                </h2>
                <div className="mt-2 grid grid-cols-7">
                  {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d, i) => (
                    <span
                      key={d}
                      className="font-semibold text-center"
                      style={{
                        color:
                          i === 6 ? "rgb(239, 68, 68)" : "rgb(255, 255, 255)",
                        fontSize: "11px",
                        opacity: i === 6 ? 1 : 0.9,
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="grid grid-cols-7">
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  <div className="h-9"></div>
                  <div className="flex items-center justify-center h-9">
                    <span
                      className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: "rgb(249, 115, 22)" }}
                    >
                      1
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span
                      className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: "rgb(249, 115, 22)" }}
                    >
                      2
                    </span>
                  </div>
                  <div className="flex items-center justify-center h-9">
                    <span
                      className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: "rgb(126, 34, 206)" }}
                    >
                      3
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-7">
                  {[4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[11, 12, 13, 14, 15, 16, 17].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[18, 19].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  {[20, 21, 22, 23].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(249, 115, 22)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-center h-9">
                    <span
                      className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: "rgb(126, 34, 206)" }}
                    >
                      24
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-7">
                  {[25, 26, 27, 28, 29, 30, 31].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl overflow-hidden shadow-md"
              style={{ backgroundColor: "rgb(253, 242, 248)" }}
            >
              <div
                className="relative pt-3 pb-3 px-4"
                style={{ backgroundColor: "rgb(0, 0, 0)" }}
              >
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: "rgb(251, 207, 232)",
                        border: "2px solid rgb(244, 114, 182)",
                      }}
                    ></div>
                  ))}
                </div>
                <h2
                  className="text-center text-2xl font-extrabold tracking-wide mt-4"
                  style={{ color: "rgb(255, 255, 255)" }}
                >
                  FEBRUARY
                </h2>
                <div className="mt-2 grid grid-cols-7">
                  {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d, i) => (
                    <span
                      key={d}
                      className="font-semibold text-center"
                      style={{
                        color:
                          i === 6 ? "rgb(239, 68, 68)" : "rgb(255, 255, 255)",
                        fontSize: "11px",
                        opacity: i === 6 ? 1 : 0.9,
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="grid grid-cols-7">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[8, 9, 10, 11, 12, 13, 14].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[15, 16, 17].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                  {[18, 19, 20, 21].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(126, 34, 206)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {[22, 23, 24, 25, 26, 27, 28].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div
              className="rounded-2xl overflow-hidden shadow-md"
              style={{ backgroundColor: "rgb(253, 242, 248)" }}
            >
              <div
                className="relative pt-3 pb-3 px-4"
                style={{ backgroundColor: "rgb(0, 0, 0)" }}
              >
                <div className="absolute -top-1 left-0 right-0 flex justify-center gap-4 px-6">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-full"
                      style={{
                        backgroundColor: "rgb(251, 207, 232)",
                        border: "2px solid rgb(244, 114, 182)",
                      }}
                    ></div>
                  ))}
                </div>
                <h2
                  className="text-center text-2xl font-extrabold tracking-wide mt-4"
                  style={{ color: "rgb(255, 255, 255)" }}
                >
                  MARCH
                </h2>
                <div className="mt-2 grid grid-cols-7">
                  {["MO", "TU", "WE", "TH", "FR", "SA", "SU"].map((d, i) => (
                    <span
                      key={d}
                      className="font-semibold text-center"
                      style={{
                        color:
                          i === 6 ? "rgb(239, 68, 68)" : "rgb(255, 255, 255)",
                        fontSize: "11px",
                        opacity: i === 6 ? 1 : 0.9,
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="grid grid-cols-7">
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <div
                      key={n}
                      className="flex items-center justify-center h-9"
                    >
                      <span
                        className="text-white text-[13px] font-semibold w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: "rgb(4, 120, 87)" }}
                      >
                        {n}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>{" "}
          <div className="flex flex-wrap items-center justify-center gap-6 p-3 bg-pink-50/60 rounded-xl border border-pink-100 shadow-sm">
            <div className="flex items-center gap-2">
              <span
                className="w-3.5 h-3.5 rounded-full inline-block shadow-sm"
                style={{ backgroundColor: "rgb(4, 120, 87)" }}
              ></span>
              <span className="text-xs font-semibold text-gray-800">
                Economy Dates
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-3.5 h-3.5 rounded-full inline-block shadow-sm"
                style={{ backgroundColor: "rgb(126, 34, 206)" }}
              ></span>
              <span className="text-xs font-semibold text-gray-800">
                Regular Dates
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="w-3.5 h-3.5 rounded-full inline-block shadow-sm"
                style={{ backgroundColor: "rgb(249, 115, 22)" }}
              ></span>
              <span className="text-xs font-semibold text-gray-800">
                Premium Dates
              </span>
            </div>
          </div>
        </div> */}

      {/* ────────────────── DYNAMIC API SECTION: PACKAGE WISE ITINERARY (FLAT) ────────────────── */}
      {resort.PackageWiseResortItinerary &&
        resort.PackageWiseResortItinerary.length > 0 && (
          <section className="py-20 bg-gray-50/50 border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-2xl text-gray-900 mb-3 font-semibold">
                  Detailed Day-by-Day Itineraries
                </h2>
                <p className="text-gray-600 font-normal text-sm leading-relaxed">
                  Take a look at our carefully structured daily schedules,
                  curated to give you the perfect balance of adventure and
                  relaxation.
                </p>
              </div>

              {/* Package Tabs */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {resort.PackageWiseResortItinerary.map((itinerary, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setActiveItineraryIndex(idx);
                      setActiveDayIndex(0);
                    }}
                    type="button"
                    className={`px-6 py-3 rounded-full text-sm font-bold tracking-wide transition duration-300 cursor-pointer ${
                      activeItineraryIndex === idx
                        ? "bg-pink-600 text-white shadow-md"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {itinerary.PackageTitle}
                  </button>
                ))}
              </div>

              {/* Selected Package Days */}
              {resort.PackageWiseResortItinerary[activeItineraryIndex]
                ?.Days && (
                <div className="bg-white rounded-xl p-6 sm:p-10 border border-gray-100 shadow-sm">
                  {/* Day Selector Buttons */}
                  <div className="flex border-b border-gray-100 mb-8 overflow-x-auto gap-8">
                    {resort.PackageWiseResortItinerary[
                      activeItineraryIndex
                    ].Days.map((day, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveDayIndex(idx)}
                        type="button"
                        className={`pb-4 text-sm font-semibold tracking-wide relative whitespace-nowrap transition cursor-pointer ${
                          activeDayIndex === idx
                            ? "text-pink-600"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        Day {day.Day}
                        {activeDayIndex === idx && (
                          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-600 rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Timeline */}
                  <div className="space-y-6 py-2">
                    {resort.PackageWiseResortItinerary[
                      activeItineraryIndex
                    ].Days[activeDayIndex]?.Data?.map((item, idx) => (
                      <div key={idx} className="relative group">
                        <div
                          className="text-gray-700 font-normal text-sm sm:text-base leading-relaxed select-text blog-content"
                          dangerouslySetInnerHTML={{ __html: item.Itinerary }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

      {/* ────────────────── GRID LAYOUT: MAIN CONTENT VS STICKY SIDEBAR (FLAT) ────────────────── */}
      <section className="py-20 bg-white border-b border-gray-150">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* LEFT 2-COLUMNS: MAIN DYNAMIC CONTENT */}
            <div className="lg:col-span-2 space-y-12">
              {/* DYNAMIC API: PHOTO GALLERY (FLAT) */}
              {gallery.length > 0 && (
                <div>
                  <div className="mb-6 flex justify-between items-end">
                    <h2 className="text-2xl text-gray-900 font-semibold">
                      Photo Gallery
                    </h2>
                    <button
                      onClick={() => {
                        setSelectedImageIndex(0);
                        setGalleryOpen(true);
                      }}
                      type="button"
                      className="text-xs font-semibold text-pink-600 hover:text-pink-700 transition cursor-pointer"
                    >
                      View All ({gallery.length})
                    </button>
                  </div>

                  <div className="grid grid-cols-6 gap-3">
                    {gallery.slice(0, 5).map((image, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setGalleryOpen(true);
                        }}
                        className={`relative overflow-hidden rounded-xl cursor-pointer group bg-gray-100 border border-gray-100 ${
                          index === 0
                            ? "col-span-6 md:col-span-4 row-span-2 h-72 sm:h-80"
                            : "col-span-3 md:col-span-2 h-36"
                        }`}
                      >
                        <ImageWithFallback
                          src={image}
                          alt={`Resort gallery image ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-300"></div>
                      </div>
                    ))}

                    {/* More Photos trigger box */}
                    {gallery.length > 5 && (
                      <button
                        onClick={() => {
                          setSelectedImageIndex(5);
                          setGalleryOpen(true);
                        }}
                        type="button"
                        className="col-span-3 md:col-span-2 h-36 bg-gray-900 relative rounded-xl overflow-hidden flex flex-col items-center justify-center group cursor-pointer text-center px-4"
                      >
                        <ImageWithFallback
                          src={gallery[5] || gallery[0]}
                          alt="Gallery remaining cover"
                          className="absolute inset-0 w-full h-full object-cover opacity-35 group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gray-950/40"></div>
                        <span className="relative text-white font-semibold text-base tracking-wide z-10">
                          +{gallery.length - 5} more photos
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT 1-COLUMN: STICKY BOOKING SIDEBAR */}
            <div className="lg:col-span-1">
              <div
                id="booking-sidebar"
                className="sticky top-32 bg-white rounded-xl p-6 shadow-lg border-2 border-gray-100"
              >
                <h3 className="text-2xl text-gray-900 mb-6 font-semibold">
                  Book Your Stay
                </h3>

                <form onSubmit={formik.handleSubmit} className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Full Name *
                    </label>
                    <input
                      name="FirstName"
                      type="text"
                      placeholder="Your Full Name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.FirstName}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.FirstName && formik.errors.FirstName
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none transition-all duration-300 bg-white text-sm text-gray-800 font-normal`}
                    />
                    {formik.touched.FirstName && formik.errors.FirstName && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.FirstName as string}
                      </span>
                    )}
                  </div>

                  {/* Email ID */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      name="EmailID"
                      type="email"
                      placeholder="your@email.com"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.EmailID}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.EmailID && formik.errors.EmailID
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none transition-all duration-300 bg-white text-sm text-gray-800 font-normal`}
                    />
                    {formik.touched.EmailID && formik.errors.EmailID && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.EmailID as string}
                      </span>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Phone Number *
                    </label>
                    <input
                      name="MobileNo"
                      type="tel"
                      maxLength={10}
                      placeholder="10-digit mobile no"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.MobileNo}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.MobileNo && formik.errors.MobileNo
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none transition-all duration-300 bg-white text-sm text-gray-800 font-normal`}
                    />
                    {formik.touched.MobileNo && formik.errors.MobileNo && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.MobileNo as string}
                      </span>
                    )}
                  </div>

                  {/* Check In Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Check-in Date *
                    </label>
                    <input
                      name="CheckIn"
                      type="date"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.CheckIn}
                      className={`w-full px-4 py-3 border-2 ${
                        formik.touched.CheckIn && formik.errors.CheckIn
                          ? "border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                      } rounded-xl outline-none bg-white text-sm text-gray-800 font-normal`}
                      min={new Date().toISOString().split("T")[0]}
                    />
                    {formik.touched.CheckIn && formik.errors.CheckIn && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {formik.errors.CheckIn as string}
                      </span>
                    )}
                  </div>

                  {/* Nights */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Nights *
                    </label>
                    <select
                      name="Nights"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.Nights}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none bg-white text-sm text-gray-800 font-normal cursor-pointer"
                    >
                      <option value="1 Nights">1 Nights</option>
                      <option value="2 Nights">2 Nights</option>
                      <option value="3 Nights">3 Nights</option>
                    </select>
                  </div>

                  {/* Adults & Child grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Adults *
                      </label>
                      <select
                        name="Adults"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.Adults}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none bg-white text-sm text-gray-800 font-normal cursor-pointer"
                      >
                        {Array.from({ length: 20 }, (_, i) => i + 1).map(
                          (n) => (
                            <option key={n} value={String(n)}>
                              {n} {n === 1 ? "Adult" : "Adults"}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Child
                      </label>
                      <select
                        name="Child"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.Child}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none bg-white text-sm text-gray-800 font-normal cursor-pointer"
                      >
                        {Array.from({ length: 21 }, (_, i) => i).map((n) => (
                          <option key={n} value={String(n)}>
                            {n} {n === 1 ? "Child" : "Children"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Submit Trigger */}
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="w-full bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white px-6 py-4 rounded-xl hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all duration-300 cursor-pointer font-bold text-sm flex items-center justify-center gap-2"
                  >
                    {formik.isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        <span>Sending Inquiry...</span>
                      </>
                    ) : (
                      <span>Send Inquiry</span>
                    )}
                  </button>
                  {resort.BookingURL && (
                    <button
                      type="button"
                      onClick={() => {
                        insertBookingReference(0, window.location.href).catch(
                          () => {},
                        );
                        window.open(
                          resort.BookingURL,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                      className="w-full border-2 border-pink-500 text-pink-600 px-6 py-4 rounded-xl hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all duration-300 cursor-pointer font-bold text-sm flex items-center justify-center gap-2"
                    >
                      Book Now
                    </button>
                  )}
                </form>

                {/* Inquiry details / Support card details */}
                {/* <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg mb-4 text-gray-900 font-semibold">
                    More Inquiry
                  </h4>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-550" />
                      <Link
                        href="tel:+919099667119"
                        className="hover:text-pink-600 transition-colors"
                      >
                        +91 9099667119
                      </Link>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-gray-550" />
                      <Link
                        href="mailto:booking@aalpine.in"
                        className="hover:text-pink-600 transition-colors"
                      >
                        booking@aalpine.in
                      </Link>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-gray-550 mt-1 flex-shrink-0" />
                      <span className="leading-relaxed">
                        B-701/702, PNTC, Radio Mirchi Road, Satellite, Ahmedabad
                        - 380015
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-green-600 font-semibold pt-1.5">
                      <MessageCircle className="w-4 h-4 text-green-550" />
                      <Link
                        href="https://wa.me/919099667119"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-green-700 transition"
                      >
                        Chat on WhatsApp
                      </Link>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────── DYNAMIC API SECTION: ROOM TYPES SLIDER (FLAT) ────────────────── */}
      {rooms.length > 0 && (
        <section className="py-20 bg-gray-50/30 border-b border-gray-150 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Room Types & Pricing
              </h2>
              <p className="text-gray-600 font-normal text-sm">
                Explore our luxurious accommodation options beautifully
                integrated with local style and modern premium amenities.
              </p>
            </div>

            <div className="relative px-0 sm:px-8">
              <Slider
                {...{
                  dots: true,
                  infinite: rooms.length > slidesCount.rooms,
                  speed: 500,
                  slidesToShow: slidesCount.rooms,
                  slidesToScroll: 1,
                  autoplay: rooms.length > slidesCount.rooms,
                  autoplaySpeed: 5000,
                  swipe: true,
                  touchMove: true,
                  arrows: slidesCount.rooms > 1 && rooms.length > 1,
                  nextArrow:
                    slidesCount.rooms > 1 && rooms.length > 1 ? (
                      <NextArrow />
                    ) : undefined,
                  prevArrow:
                    slidesCount.rooms > 1 && rooms.length > 1 ? (
                      <PrevArrow />
                    ) : undefined,
                }}
              >
                {rooms.map((room, index) => (
                  <div key={index} className="px-1 sm:px-3 pb-4">
                    <div className="group rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col justify-between hover:border-pink-200 hover:shadow-lg transition-all duration-300 h-[410px]">
                      <div>
                        <div className="h-56 overflow-hidden bg-gray-100 relative">
                          <ImageWithFallback
                            src={room.MainImage || "/placeholder.png"}
                            alt={room.Title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-5 flex-grow flex flex-col justify-between">
                          <div>
                            <h4 className="text-xl font-semibold text-gray-800 mb-3 group-hover:text-pink-600 transition-colors">
                              {room.Title}
                            </h4>
                            <div
                              className="text-xs font-normal text-gray-600 leading-relaxed line-clamp-5"
                              dangerouslySetInnerHTML={{
                                __html: room.Description || "",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      )}
      {/* ────────────────── DYNAMIC API SECTION: AMENITIES GRID (FLAT) ────────────────── */}
      {displayAmenities.length > 0 && (
        <section className="py-20 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Amenities & Facilities
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Indulge in modern comforts and hospitality designed to make your
                desert stay unforgettable.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {displayAmenities.map((amenity: any, idx) => {
                const IconComponent = amenity.Icon;
                return (
                  <div
                    key={idx}
                    className="group flex flex-col items-center text-center p-6 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md hover:border-pink-100 transition-all duration-300"
                  >
                    <div className="w-12 h-12 text-pink-650 flex items-center justify-center mb-4 transition-colors group-hover:text-pink-700">
                      {IconComponent ? (
                        <IconComponent className="w-8 h-8" />
                      ) : (
                        <div className="relative w-8 h-8 overflow-hidden rounded-full">
                          <ImageWithFallback
                            src={amenity.Image}
                            alt={amenity.Title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-gray-700 font-semibold mb-1 group-hover:text-pink-600 transition-colors">
                      {amenity.Title}
                    </span>
                    {amenity.Description ? (
                      <div
                        className="text-xs text-gray-500 font-normal leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: amenity.Description,
                        }}
                      />
                    ) : (
                      <span className="text-xs text-gray-400 font-normal">
                        Verified Facility
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: STAY POLICIES & GUIDELINES TABBED CONTAINER ────────────────── */}
      {infoTabs.length > 0 && (
        <section className="py-20 bg-white border-b border-gray-150">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Tabs Header bar */}
            <div className="flex border-b border-gray-200 overflow-x-auto gap-2 sm:gap-6 mb-8 scrollbar-none">
              {infoTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveInfoTab(tab.id)}
                  type="button"
                  className={`pb-4 text-xs sm:text-sm font-semibold tracking-wider relative whitespace-nowrap transition cursor-pointer uppercase ${
                    activeInfoTab === tab.id ||
                    (!activeInfoTab && tab.id === infoTabs[0].id)
                      ? "text-pink-600 font-bold"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {tab.title}
                  {(activeInfoTab === tab.id ||
                    (!activeInfoTab && tab.id === infoTabs[0].id)) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Active Tab Panel Content */}
            {activeTabObj && (
              <div
                className={`p-6 sm:p-10 rounded-2xl border transition-all duration-300 ${
                  activeTabObj.isCancellation
                    ? "bg-amber-50/10 border-amber-200/50 shadow-sm"
                    : "bg-gray-50 border-gray-100 shadow-sm"
                }`}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-6 tracking-wide">
                  {activeTabObj.title}
                </h3>
                <div className="prose max-w-none">
                  <DynamicParsedHtml html={activeTabObj.content} />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: TESTIMONIALS / GUEST REVIEWS (FLAT / NO CARD WRAP) ────────────────── */}
      {displayTestimonials.length > 0 && (
        <section className="py-20 bg-gray-50/50 border-b border-gray-150 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Guest Reviews
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Read authentic stories and experiences shared by travelers who
                stayed at our luxury desert resort.
              </p>
            </div>

            <div className="relative px-6">
              <Slider {...testimonialSliderSettings}>
                {displayTestimonials.map((review, index) => (
                  <div key={index} className="px-4 pb-4">
                    <div className="bg-white p-8 rounded-xl border border-gray-200 h-[380px] flex flex-col justify-between hover:shadow-md transition duration-300">
                      <div>
                        {/* Quote Icon */}
                        <div className="mb-4">
                          <Quote className="w-10 h-10 text-pink-500 opacity-20" />
                        </div>

                        {/* Rating */}
                        <div className="flex gap-1 mb-4">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-4.5 h-4.5 text-yellow-500 fill-current"
                            />
                          ))}
                        </div>

                        {/* Text */}
                        <p className="text-gray-700 mb-6 italic text-sm sm:text-base leading-relaxed font-normal line-clamp-4">
                          "{review.text}"
                        </p>
                      </div>

                      <div>
                        {review.package && (
                          <p className="text-xs text-pink-600 mb-4 font-semibold tracking-wide">
                            {review.package}
                          </p>
                        )}

                        {/* Author */}
                        <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                          {review.image && (
                            <ImageWithFallback
                              src={review.image}
                              alt={review.name}
                              className="w-12 h-12 rounded-full object-cover shadow-sm border border-gray-200"
                            />
                          )}
                          <div>
                            <p className="text-gray-900 font-semibold text-sm sm:text-base">
                              {review.name}
                            </p>
                            {review.type && (
                              <p className="text-xs text-gray-500">
                                {review.type}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: ACTIVITIES & EXPERIENCES (FLAT) ────────────────── */}
      {displayActivities.length > 0 && (
        <section className="py-20 bg-white border-b border-gray-150 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Resort Experiences & Activities
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Immerse yourself in Kutch's vibrant culture, adventure
                activities, and local entertainment.
              </p>
            </div>

            <div className="relative px-0 sm:px-8">
              <Slider
                {...{
                  dots: true,
                  infinite: displayActivities.length > slidesCount.activities,
                  speed: 500,
                  slidesToShow: slidesCount.activities,
                  slidesToScroll: 1,
                  autoplay: displayActivities.length > slidesCount.activities,
                  autoplaySpeed: 5000,
                  swipe: true,
                  touchMove: true,
                  arrows:
                    slidesCount.activities > 1 && displayActivities.length > 1,
                  nextArrow:
                    slidesCount.activities > 1 &&
                    displayActivities.length > 1 ? (
                      <NextArrow />
                    ) : undefined,
                  prevArrow:
                    slidesCount.activities > 1 &&
                    displayActivities.length > 1 ? (
                      <PrevArrow />
                    ) : undefined,
                }}
              >
                {displayActivities.map((act, index) => (
                  <div key={index} className="px-1 sm:px-3 pb-4">
                    <div className="group relative rounded-xl overflow-hidden h-60 bg-slate-950 border border-gray-100 shadow-md">
                      {act.image && (
                        <ImageWithFallback
                          src={act.image}
                          alt={act.title}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent"></div>
                      {act.tag && (
                        <span className="absolute top-4 left-4 bg-black/45 backdrop-blur-md text-white text-[9px] uppercase tracking-widest px-3 py-1.5 rounded font-semibold border border-white/10 shadow-sm z-10">
                          {act.tag}
                        </span>
                      )}
                      <div className="absolute bottom-5 left-5 right-5 text-white">
                        <h4 className="text-base font-semibold tracking-wide mb-1 flex items-center gap-1 group-hover:text-pink-300 transition-colors">
                          {act.title}
                          <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                        </h4>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: FAQ ACCORDION (FLAT) ────────────────── */}
      {displayFaqs.length > 0 && (
        <section className="py-20 bg-gray-50/50 border-b border-gray-150">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Everything you need to know about booking, amenities, stay
                policies, and desert guidelines.
              </p>
            </div>

            <div className="space-y-4 w-full">
              {displayFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 bg-white rounded-xl overflow-hidden hover:border-pink-200 transition-colors"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    type="button"
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition cursor-pointer font-semibold"
                  >
                    <span className="text-lg pr-4 text-gray-800">
                      {faq.question}
                    </span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-pink-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-gray-650 font-normal text-sm sm:text-base leading-relaxed border-t border-gray-100 pt-4">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ────────────────── DYNAMIC API SECTION: SCENIC NEARBY ATTRACTIONS (FLAT / CONDITIONALLY SHOWN) ────────────────── */}
      {resort.AttractionsList && resort.AttractionsList.length > 0 && (
        <section className="py-20 bg-white border-b border-gray-150 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Scenic Nearby Attractions
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Discover Kutch's historical landmarks, breathtaking viewpoints,
                and cultural heritage sites near the resort.
              </p>
            </div>

            <div className="relative px-0 sm:px-8">
              <Slider
                dots={true}
                infinite={
                  resort.AttractionsList.length > slidesCount.attractions
                }
                speed={500}
                slidesToShow={slidesCount.attractions}
                slidesToScroll={1}
                autoplay={
                  resort.AttractionsList.length > slidesCount.attractions
                }
                autoplaySpeed={5000}
                swipe={true}
                touchMove={true}
                arrows={
                  slidesCount.attractions > 1 &&
                  resort.AttractionsList.length > 1
                }
                nextArrow={
                  slidesCount.attractions > 1 &&
                  resort.AttractionsList.length > 1 ? (
                    <NextArrow />
                  ) : undefined
                }
                prevArrow={
                  slidesCount.attractions > 1 &&
                  resort.AttractionsList.length > 1 ? (
                    <PrevArrow />
                  ) : undefined
                }
              >
                {resort.AttractionsList.map((att, index) => {
                  const attImage =
                    att.MainImage || att.Image || "/placeholder.png";
                  const cleanedDesc = getCleanText(att.Description || "");
                  const attUrl =
                    att.AttractionURL ||
                    att.URL ||
                    att.AttractionUrl ||
                    att.Url;

                  return (
                    <div key={index} className="px-3 pb-4">
                      <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col justify-between h-full min-h-[400px]">
                        {/* Top Section */}
                        <div>
                          {/* Image */}
                          {attImage && (
                            <div className="relative h-48 overflow-hidden">
                              <ImageWithFallback
                                src={attImage}
                                alt={att.Title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                              {/* Distance Badge */}
                              {att.Distance && (
                                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2 shadow-sm z-10">
                                  <MapPin className="w-4 h-4 text-pink-600" />
                                  <span className="text-sm font-sans font-medium text-gray-800">
                                    {att.Distance}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Content */}
                          <div className="p-6">
                            <h3 className="text-xl font-sans font-semibold text-gray-800 mb-2 group-hover:text-pink-600 transition duration-300">
                              {att.Title}
                            </h3>

                            {/* Travel Time */}
                            {att.TravelTime && (
                              <div className="flex items-center gap-2 text-gray-500 mb-3">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className="text-sm font-sans font-normal">
                                  {att.TravelTime} from tent city
                                </span>
                              </div>
                            )}

                            {/* Description */}
                            {cleanedDesc && (
                              <p className="text-gray-600 text-sm font-sans font-normal leading-relaxed mb-2 line-clamp-3">
                                {cleanedDesc}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Action Button */}
                        {attUrl && (
                          <div className="px-6 pb-6">
                            <Link
                              href={`/travel-guide/${attUrl}`}
                              className="text-pink-600 hover:text-pink-700 transition cursor-pointer font-sans font-medium text-sm inline-flex items-center gap-1 group/btn"
                            >
                              Learn More
                              <span className="group-hover/btn:translate-x-1 transition-transform">
                                →
                              </span>
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Slider>
            </div>
          </div>
        </section>
      )}

      {/* ────────────────── INTERACTIVE LOCATION MAP (FLAT / CONDITIONALLY SHOWN) ────────────────── */}
      {resort.MapURL && resort.MapURL.trim() !== "" && (
        <section className="py-20 bg-gray-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-2xl text-gray-900 mb-2 font-semibold">
                Interactive Location Map
              </h2>
              <p className="text-gray-600 font-normal text-sm leading-relaxed">
                Find the exact geographical coordinate of our glamping resort
                and plan your navigation seamlessly.
              </p>
            </div>

            <div className="w-full h-[300px] rounded-xl overflow-hidden shadow-md border border-gray-200 bg-gray-150">
              <iframe
                src={cleanMapUrl(resort.MapURL)}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${resort.Title} Location Map`}
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* Image Gallery Modal overlay */}
      <ImageGalleryModal
        images={gallery}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
}
